// src/hooks/useInventoryBranchPermissions.ts
import { useMemo } from 'react';
import { usePermission } from './usePermission';
import { INVENTORY_PERMISSIONS } from '../constants/permissions.constants';

/**
 * Hook to get all inventory branch-related permissions in one object
 *
 * Used for branch inventory operations: view, edit, transfer, reserve, unreserve
 *
 * @returns Object with all branch inventory permission flags
 *
 * @example
 * ```tsx
 * const branchPerms = useInventoryBranchPermissions();
 * if (branchPerms.canEdit) {
 *   // Show edit button
 * }
 * ```
 */
export const useInventoryBranchPermissions = () => {
  const canView = usePermission(INVENTORY_PERMISSIONS.BRANCH.VIEW);
  const canEdit = usePermission(INVENTORY_PERMISSIONS.BRANCH.EDIT);
  const canTransfer = usePermission(INVENTORY_PERMISSIONS.BRANCH.TRANSFER);
  const canReserve = usePermission(INVENTORY_PERMISSIONS.BRANCH.RESERVE);
  const canUnreserve = usePermission(INVENTORY_PERMISSIONS.BRANCH.UNRESERVE);

  return useMemo(
    () => ({
      canView,
      canEdit,
      canTransfer,
      canReserve,
      canUnreserve,
    }),
    [canView, canEdit, canTransfer, canReserve, canUnreserve],
  );
};
